/**
 * The arithmetic behind a calorie change typed as a percentage (#5456, phase 2).
 *
 * A coach, or a person without a coach, sets a daily calorie target either as a number of kcal or
 * as a change in percent. The percent is always relative to the SAME base: the intake the person
 * reported at their check-up, which is also the number the app's own recommendation steps from.
 *
 * Example: BORI reported eating 2,178 kcal a day. "Decrease 5%" is 2,069 kcal. Typing 2,120 kcal
 * reads back as "Decrease 2.7%".
 *
 * Kept free of Angular so a unit test can drive it (`calorie-change.test.ts`). The control that
 * uses it is `cs-calorie-change-input`. See design-docs/coaching-dashboard.md, "The % change".
 */

/** which way a calorie change goes, relative to the reported intake */
export enum CalorieChangeDirection {
  Decrease = 'Decrease',
  Increase = 'Increase',
}

/** a calorie change as the control shows it: a direction and an unsigned percent */
export interface CalorieChange {
  /** undefined when the target equals the base: there is no direction to derive, so the caller keeps its switch where it is */
  direction: CalorieChangeDirection | undefined;
  /** the size of the change in percent of the base, rounded to 1 decimal; never negative */
  percent: number;
}

/** true for a real number: not NaN, not infinite, not undefined */
function isUsableNumber(value: number | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** true for a real number above zero: what a base, a target or a minimum must be before any maths is done with it */
export function isPositiveNumber(value: number | undefined): value is number {
  return isUsableNumber(value) && value > 0;
}

/**
 * The direction and percent that turn `baseCalories` into `calories`.
 *
 * Returns the true percent even above 100 (a target more than twice the base); what the control
 * shows then is its own rule. Undefined when the base is not a positive number or `calories` is
 * not a number.
 */
export function calorieChangeFromCalories(baseCalories: number | undefined, calories: number | undefined): CalorieChange | undefined {
  if (!isPositiveNumber(baseCalories) || !isUsableNumber(calories)) {
    return undefined;
  }
  const percent = Math.round(Math.abs(calories - baseCalories) / baseCalories * 1000) / 10;
  let direction: CalorieChangeDirection | undefined;
  if (calories > baseCalories) {
    direction = CalorieChangeDirection.Increase;
  } else if (calories < baseCalories) {
    direction = CalorieChangeDirection.Decrease;
  }
  return { direction, percent };
}

/**
 * The daily target a change of `percent` in `direction` gives, in whole kcal.
 * Undefined when the base is not a positive number, the percent is not a number or is negative,
 * or no direction is given.
 */
export function caloriesFromChange(
  baseCalories: number | undefined,
  direction: CalorieChangeDirection | undefined,
  percent: number | undefined,
): number | undefined {
  if (!isPositiveNumber(baseCalories) || !isUsableNumber(percent) || percent < 0 || direction === undefined) {
    return undefined;
  }
  const sign = direction === CalorieChangeDirection.Increase ? 1 : -1;
  return Math.round(baseCalories * (1 + sign * percent / 100));
}

/**
 * Whether a typed target is below the lowest daily target the app itself would set for this
 * person. True only when both are positive numbers and, compared as whole kcal, the target is
 * lower. A check-up that stored no such minimum never warns.
 */
export function isBelowMinimumIntake(calories: number | undefined, minimumDailyIntake: number | undefined): boolean {
  if (!isPositiveNumber(calories) || !isPositiveNumber(minimumDailyIntake)) {
    return false;
  }
  return Math.round(calories) < Math.round(minimumDailyIntake);
}
